"use client";

import { useEffect, useState } from "react";
import { Notice } from "@/components/tool-kit/primitives";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { readStored, writeStored } from "@/lib/tool-kit/study/safe-storage";

const STORAGE_KEY = "citationeasy-notepad-v1";

const WORD_SPLIT_REGEX = /\s+/;

function countWords(text: string): number {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return 0;
  }
  return trimmed.split(WORD_SPLIT_REGEX).length;
}

function countLines(text: string): number {
  if (text.length === 0) {
    return 0;
  }
  return text.split("\n").length;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const ONLINE_NOTEPAD_STRINGS = {
  notesLabel: "Your notes — autosaved to this browser",
  inputPlaceholder:
    "Start typing — your notes save automatically as you write.",
  downloadTxt: "Download .txt",
  copied: "Copied!",
  copyAll: "Copy All",
  savingUnavailable:
    "Saving is unavailable: this browser is blocking local storage, so your notes will be lost when you leave or reload the page. Use Download .txt to keep a copy.",
  clear: "Clear",
  reallyClear: "Really clear?",
  words: "Words",
  characters: "Characters",
  lines: "Lines",
  privacyHeading: "Your Notes Stay on Your Device",
  privacyBody:
    "Everything you type is stored only in this browser, on this device, using local storage. Nothing is uploaded to a server and we cannot see what you write. That also means your notes won't follow you to another browser or device, and clearing your browsing data will erase them — so browser storage is not a substitute for real backups. For anything you can't afford to lose, use Download .txt and keep a copy somewhere you back up.",
} as const;

export default function OnlineNotepadWidget() {
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [clearArmed, setClearArmed] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const strings = useToolUi(ONLINE_NOTEPAD_STRINGS);

  const fmt = useToolNumber();

  useEffect(() => {
    const saved = readStored(STORAGE_KEY);
    if (!saved.available) {
      setSaveFailed(true);
    }
    if (saved.value !== null) {
      setText(saved.value);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) {
      return;
    }
    const id = setTimeout(() => {
      setSaveFailed(!writeStored(STORAGE_KEY, text));
    }, 500);
    return () => clearTimeout(id);
  }, [text, loaded]);

  function handleDownload() {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "notes.txt";
    anchor.click();
    URL.revokeObjectURL(url);
  }

  function handleCopy() {
    if (text.length === 0) {
      return;
    }
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setText("");
    setSaveFailed(!writeStored(STORAGE_KEY, ""));
    setClearArmed(false);
    setCopied(false);
  }

  function handleTextChange(value: string) {
    setText(value);
    setClearArmed(false);
  }

  const clearLabel = clearArmed ? strings.reallyClear : strings.clear;

  return (
    <>
      {/* Notepad */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="notepad-input"
        >
          {strings.notesLabel}
        </label>
        <textarea
          className="min-h-[320px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="notepad-input"
          onChange={(e) => handleTextChange(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={text}
        />
      </div>

      {saveFailed && (
        <output className="block">
          <Notice>{strings.savingUnavailable}</Notice>
        </output>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={text.length === 0}
          onClick={handleDownload}
          type="button"
        >
          {strings.downloadTxt}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={text.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyAll}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={text.length === 0}
          onClick={handleClear}
          type="button"
        >
          {clearLabel}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label={strings.words} value={fmt(countWords(text))} />
        <StatCard label={strings.characters} value={fmt(text.length)} />
        <StatCard label={strings.lines} value={fmt(countLines(text))} />
      </div>

      {/* Privacy */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.privacyHeading}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.privacyBody}
        </p>
      </section>
    </>
  );
}
